import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { ResourceData } from '../elements/resource/resource.types';
import {
  selectSlides,
  selectProjects,
  selectEditorFeature,
  selectSceneFiles,
  selectInputFiles,
  selectCurrentProjectFromEditor,
  selectSelectedScenes,
  selectSceneSelectionInterval,
} from '../element-editor/store/selector/editor.selector';
import { IdToIRI } from '../util/iri';
import { UploadedImage } from './image-upload-modal/image-upload-modal.component';
import { CanvasService } from '../services/canvas/canvas.service';
import { AnimationService } from '../element-editor/animation/animation.service';
import {
  loadProject,
  selectSelectedMedia,
  selectStudioAppState,
  StudioService,
} from '../element-editor/store/studio';
import { StudioSceneShape } from '../element-editor/shape/shapes/general/root/studio-scene-shape';
import { StudioSceneBackgroundDefinition } from '../element-editor/store/studio/studio.types';
import { SceneCell } from '../elements/resource/types/shape.type';
import { ErrorService } from '../services/error/error.service';
import { RecorderService } from '../element-editor/shape/recorder.service';
type Preview =
  | {
      text?: string;
      images?: string[];
      video?: string;
    }
  | undefined;

type FileMedia = {
  id?: string;
  url?: string;
  type?: string;
  mimeType?: string;
};
type FileDescriptorContent = { text?: string; media?: FileMedia[] };

@Component({
  selector: 'app-project-overview',
  templateUrl: './project-overview.component.html',
  styleUrls: ['./project-overview.component.scss'],
})
export class ProjectOverviewComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('middle') middle: any;

  projectID = '';
  loading = false;
  errorMsg = '';
  slides$: Observable<ResourceData[]> = this.store.select(selectSlides);
  projects$ = this.store.select(selectProjects);
  projectId$ = this.route.paramMap.pipe(map(pm => pm.get('projectID') || ''));
  projectPreviewIsLoading$ = this.store
    .select(selectEditorFeature)
    .pipe(map(s => !!s.projectPreviewIsLoading));
  projectLabel$: Observable<string | undefined> = combineLatest([
    this.projects$,
    this.projectId$,
  ]).pipe(
    map(
      ([projects, pid]) =>
        (projects?.[IdToIRI(pid)]?.literals as { label?: string } | undefined)
          ?.label,
    ),
  );
  sceneFiles$: Observable<ResourceData[]> = this.store.select(selectSceneFiles);
  inputFiles$: Observable<ResourceData[]> = this.store.select(selectInputFiles);
  // StudioSceneShape management

  currentStudioSceneShape: StudioSceneShape | null = null;
  // Background modal state
  showBgModal = false;
  sceneIndexOptions: number[] = [];
  bgStartIndex = 1; // 1-based for UI
  bgEndIndex = 1; // 1-based for UI
  bgType: 'glow' | 'gradient' | 'static' | 'image' | 'video' = 'glow';
  // Layout modal state
  showLayoutModal = false;
  layoutStartIndex = 1;
  layoutEndIndex = 1;
  layoutType:
    | 'text'
    | 'title-subtitle'
    | 'images-2'
    | 'images-3'
    | 'image-label'
    | 'video' = 'text';
  layoutBaseMargin: 0 | 10 | 20 = 10;
  get currentTime() {
    return this.animationService.currentAnimationTime;
  }
  get totalDuration() {
    return this.animationService.totalAnimationDuration;
  }

  get canvasTop() {
    return this.cs.canvasTop;
  }
  selectedScenes$ = this.store.select(selectSelectedScenes);
  selectedScenesLength$ = this.selectedScenes$.pipe(
    map(s => Object.keys(s).length),
  );
  textEditModalConfig: {
    id: string;
    text: string;
  };
  // Image upload modal state
  showImageUploadModal = false;
  currentImageCellId: string | null = null;
  selectedMedia$ = this.store.select(selectSelectedMedia);

  isStudioProjectLoading$ = this.store
    .select(selectStudioAppState)
    .pipe(map(s => s === 'loading'));

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private readonly cs: CanvasService,
    private readonly animationService: AnimationService,

    private readonly recorderService: RecorderService,
    private readonly errorService: ErrorService,
    readonly studioService: StudioService,
  ) {
    this.cs.generalEventSubscribe('text-cell-edit', ({ id, text }) => {
      this.textEditModalConfig = { id, text };
    });

    this.cs.generalEventSubscribe('image-cell-upload', ({ id }) => {
      this.currentImageCellId = id;
      this.openImageUploadModal();
    });

    // Subscribe to all studio scenes to create StudioSceneShapes
    // Subscribe to current scene changes to switch displayed shape

    // Number one -> the project.backgroundDefinitions are changed // -- // -- //
    this.store.select(selectCurrentProjectFromEditor).subscribe(project => {
      if (project) {
        this.cs.currentProject = project;
      }
    });
  }

  export() {
    this.recorderService.export();
  }

  ngAfterViewInit() {
    // this.cs.canvasContainer.addChild(this.wrapper.nativeElement);
    const interval = setInterval(() => {
      const el = this.middle?.nativeElement;
      if (!el) return;
      // console.log('wrapper', el.offsetWidth, el.offsetHeight);
      // console.log('wrapper', el.scrollWidth, el.scrollHeight);
      const [w, h] = [el.offsetWidth, el.offsetHeight];
      this.cs._resizeCanvas(w, h);
      clearInterval(interval);
    }, 20);
    // -- //
  }
  onTextModalSave(event: string) {
    this.cs.generalEventEmit('text-cell-edit-saved', {
      id: this.textEditModalConfig.id,
      text: event,
    });
    this.textEditModalConfig = null;
  }

  onTextModalSaveFull(event: {
    id?: string;
    text: string;
    textConfig: {
      fontSize?: number;
      widthRatio?: number;
      align?: 'left' | 'center' | 'right' | 'justify';
    };
  }) {
    console.log('text-model-save-full', event);
    const id = event.id || this.textEditModalConfig?.id;
    if (!id) {
      // fallback to legacy handler
      this.onTextModalSave(event.text);
      return;
    }
    this.cs.generalEventEmit('text-cell-edit-saved', {
      id,
      text: event.text,
      textConfig: event.textConfig,
    });
    this.textEditModalConfig = null;
  }

  onTextModalCancel() {
    this.cs.generalEventEmit('text-cell-edit-saved', null);
    this.textEditModalConfig = null;
  }

  backgroundChanged() {
    // -- // -- // -- //
  }

  contentOf(file: ResourceData): FileDescriptorContent {
    const literals = file?.literals as
      | { descriptor?: { content?: FileDescriptorContent } }
      | undefined;
    return literals?.descriptor?.content ?? {};
  }

  mediaUrl(m: FileMedia | string): string {
    if (typeof m === 'string') return this.s3Url(m);
    return m?.url || this.s3Url(m?.id || '');
  }

  ngOnInit(): void {
    this.cs.studioMode = true;
    this.route.paramMap.subscribe(params => {
      const projectID = params.get('projectID') || '';
      this.projectID = projectID;
      if (projectID) {
        this.store.dispatch(loadProject({ projectID }));
      }
    });
  }

  ngOnDestroy(): void {
    this.cs.studioMode = false;
  }

  previewOf(file: ResourceData): Preview {
    const literals = (file?.literals ?? {}) as {
      descriptor?: { preview?: Preview };
    };
    return (literals.descriptor?.preview as Preview) || undefined;
  }

  openBgModal() {
    this.sceneFiles$.pipe(take(1)).subscribe(files => {
      const count = Math.max(1, (files || []).length);
      this.sceneIndexOptions = Array.from({ length: count }, (_, i) => i + 1);
      if (this.bgStartIndex > count) this.bgStartIndex = 1;
      if (this.bgEndIndex > count) this.bgEndIndex = count;
      if (this.bgStartIndex > this.bgEndIndex)
        this.bgStartIndex = this.bgEndIndex;
      this.showBgModal = true;
    });
  }

  closeBgModal() {
    this.showBgModal = false;
  }

  onBgChange(def: StudioSceneBackgroundDefinition) {
    const sceneShape = this.studioService.currentSceneShape;
    if (!sceneShape) return;
    sceneShape.scene.background = def;
    sceneShape.initBackground(def);
    sceneShape.save();
  }

  saveBgSettings() {
    // UI-only for now: log the selected range and type; apply/persist on request
    const kind = this.bgType === 'glow' ? 'ellipse' : this.bgType; // map UI label to internal
    console.log('Background settings', {
      range: [this.bgStartIndex, this.bgEndIndex],
      type: this.bgType,
      internalType: kind,
    });
    this.showBgModal = false;
  }

  onBgModalSave(e: {
    startIndex: number;
    endIndex: number;
    type: 'glow' | 'gradient' | 'static' | 'image' | 'video';
  }) {
    this.bgStartIndex = e.startIndex;
    this.bgEndIndex = e.endIndex;
    this.bgType = e.type;
    this.saveBgSettings();
  }

  // Layout modal methods
  openLayoutModal() {
    this.sceneFiles$.pipe(take(1)).subscribe(files => {
      const count = Math.max(1, (files || []).length);
      this.sceneIndexOptions = Array.from({ length: count }, (_, i) => i + 1);
      if (this.layoutStartIndex > count) this.layoutStartIndex = 1;
      if (this.layoutEndIndex > count) this.layoutEndIndex = count;
      if (this.layoutStartIndex > this.layoutEndIndex)
        this.layoutStartIndex = this.layoutEndIndex;
      this.showLayoutModal = true;
      this.cs.disableCanvasCursorEvents();
    });
  }

  closeLayoutModal() {
    this.showLayoutModal = false;
    this.cs.enableCanvasCursorEvents();
  }

  onCleanCells() {
    this.studioService.currentSceneShape?.cleanCells();
  }

  onAutoLayout() {
    this.studioService.currentSceneShape?.autoLayout();
  }

  saveLayoutSettings() {
    console.log('Layout settings', {
      range: [this.layoutStartIndex, this.layoutEndIndex],
      type: this.layoutType,
      baseMargin: this.layoutBaseMargin,
    });
    this.showLayoutModal = false;
    this.cs.enableCanvasCursorEvents();
  }

  onLayoutModalSave(e: {
    startIndex: number;
    endIndex: number;
    type:
      | 'text'
      | 'title-subtitle'
      | 'images-2'
      | 'images-3'
      | 'image-label'
      | 'video';
    baseMargin: 0 | 10 | 20;
  }) {
    this.layoutStartIndex = e.startIndex;
    this.layoutEndIndex = e.endIndex;
    this.layoutType = e.type;
    this.layoutBaseMargin = e.baseMargin;
    this.saveLayoutSettings();
  }

  s3Url(id?: string | null): string {
    return id ? `https://private.cdn.inwedio.com/${id}` : '';
  }

  openImageUploadModal() {
    this.showImageUploadModal = true;
    this.cs.disableCanvasCursorEvents();
  }

  closeImageUploadModal() {
    // Emit cancel event if there was a pending upload
    if (this.currentImageCellId) {
      this.cs.generalEventEmit('image-cell-upload-saved', null);
      this.currentImageCellId = null;
    }
    this.showImageUploadModal = false;
    this.cs.enableCanvasCursorEvents();
  }

  onImageUpload(images: UploadedImage[]) {
    console.log('Images uploaded:', images);

    if (images.length > 0 && this.currentImageCellId) {
      // For now, just use the first image
      const image = images[0];

      // Emit event with the cell id and image data
      this.cs.generalEventEmit('image-cell-upload-saved', {
        id: this.currentImageCellId,
        image: image,
      });
    }

    for (const image of images) {
      // -- //
      // this.http.saveFileToS3(image.file, id);
    }
    this.currentImageCellId = null;
    this.closeImageUploadModal();
  }

  setLayout(def: SceneCell[]) {
    console.log('setLayout', def); //
    this.cs.fileRootShape._setLayout(def);
    this.closeLayoutModal();
    return;
    // this.showLayoutModal = false;
    // -- //
    // -- //
    // Case I - I don't have any content on the slide yet
    // What I want to realize
    // -- // -- // -- //
    // -- // -- //
    // ----------------------------------- //
    // 1. Slides are just generated
    // 2. Add a column with 10 different images
    // -- // -- // -- // -- //
    // Container with cell //
    // -- // -- // -- // -- // -- //

    // default test case

    // this.cs.fileRootShape.setLayout(def);

    // switch (def.type) {
    //   case 'text':
    //     this.cs.fileRootShape.descriptor.layout = def;

    //     // -- // What should I do here // -- //
    //     // Add text-shape
    //     // and if I click on the select // -- //
    //     // -- // -- //

    //     break;
    //   case 'title-subtitle':
    //     break;
    //   // case 'images-2':
    //   //   break;
    //   // case 'images-3':
    //   //   break;
    //   // case 'image-label':
    //   //   break;
    //   case 'video':
    //     break;
    // }
  }

  playScene() {
    // this.animationService.playAnimations();
  }

  // StudioSceneShape initialization and management
}
